<template>
<div class="px-1 pt-1">
  <div class="input-group pb-3">
    <button class="btn btn-outline-secondary" @click="refresh()" :disabled="isLoading">
      <div v-if="isLoading" class="spinner-border spinner-border-sm"></div>
      <font-awesome-icon v-else :icon="['fas', 'rotate']" />
      <span class="d-none d-sm-inline">{{ $t('action.refresh') }}</span>
    </button>
    <button v-if="userSettingsStore.data.developerMode" class="btn btn-outline-primary" @click="loadNextPage()" :disabled="isLoading">
      <font-awesome-icon :icon="['fas', 'ellipsis']" />
    </button>
    <button
      v-if="userSettingsStore.data.developerMode"
      class="btn"
      :class="ignoreSignature ? 'btn-primary' : 'btn-outline-secondary'"
      :title="$t('action.ignoreSignature')"
      :aria-pressed="ignoreSignature"
      @click="ignoreSignature = !ignoreSignature"
      :disabled="isLoading">
      <font-awesome-icon :icon="['fas', 'layer-group']" />
    </button>
    <div class="flex-grow-1">
      <TypeaheadComponent 
        v-model="selectedItem" 
        :items="availableItems"
        :item-projection="itemProjection"
        :placeholder="$t('field.generic.item')"
        :disabled="isLoading"
        :min-input-length="1"
        :max-items="20"
        :input-class="'form-control'"
        @update:modelValue="onItemSelected"
      />
    </div>
    <button class="btn btn-outline-secondary" @click="resetFilter()" :disabled="isLoading" v-if="selectedItem">
      <font-awesome-icon :icon="['fas', 'filter-slash']" />
    </button>
  </div>

  <div v-if="error" class="alert alert-danger">
    {{ error }}
  </div>
  <div v-else-if="filteredList" id="content-page-table" ref="scrollContainerRef" class="table-fix-head" :style="fixHeadCalculatedStyle" @scroll.passive="onScroll">
    <table v-if="filteredList.length > 0" class="table table-hover table-sm table-responsive-lg table-fit align-middle">
      <thead>
        <tr>
          <th></th>

          <th v-if="selectedSiteId === 0">{{ $t('global.site') }}</th>
          <th>{{ $t('field.generic.item') }}</th>
          <th colspan="4">{{ $t('field.generic.quantity') }}</th>

          <th class="d-none d-lg-table-cell" v-if="!ignoreSignature">{{ $t('field.masterdata.lot') }}</th>
          <th class="d-none d-lg-table-cell" v-if="!ignoreSignature && hasBestBeforeOnAnyLine">{{ $t('field.masterdata.bestBefore') }}</th>
          <th class="d-none d-lg-table-cell" v-if="!ignoreSignature && hasExpiryOnAnyLine">{{ $t('field.masterdata.expiry') }}</th>

          <th class="d-table-cell d-lg-none" v-if="!ignoreSignature">
            {{ $t('field.masterdata.lot') }}
            <span v-if="hasBestBeforeOnAnyLine">/ {{ $t('field.masterdata.bestBefore') }}</span>
            <span v-if="hasExpiryOnAnyLine">/ {{ $t('field.masterdata.expiry') }}</span>
          </th>

          <th class="d-none d-xl-table-cell">
            {{ $t('field.generic.created') }}
            <font-awesome-icon :icon="['fas', 'sort-down']" />
          </th>
          <th v-if="userSettingsStore.data.developerMode" class="d-none d-xl-table-cell"></th>
        </tr>
      </thead>
      <tbody id="scrollable-content">
        <tr v-if="topPadHeight > 0" aria-hidden="true">
          <td :colspan="100" :style="{ padding: 0, border: 0, height: topPadHeight + 'px' }"></td>
        </tr>
        <tr v-for="stockChange in visibleRows" :key="stockChange.id" ref="rowRefs">
          <td>
            <RouterLink :to="{ name: 'stock-change', params: { itemId: stockChange.item.id } }" :title="$t('menu.stock-changes')" class="btn btn-outline-secondary btn-sm" :disabled="isLoading">
              <font-awesome-icon :icon="['fas', 'plus-minus']" />
            </RouterLink>
          </td>

          <td v-if="selectedSiteId === 0"><sup class="text-secondary">{{ stockChange.site.code }}&nbsp;</sup>{{ stockChange.site.name }}</td>
          <td><ItemField :item="stockChange.item" /></td>
          <QuantityChangeCell :stockChange="stockChange" :pad-length="maxQuantityLength" />

          <td class="d-none d-lg-table-cell" v-if="!ignoreSignature"><LotField :item="stockChange.item" :lot="stockChange.lot" /></td>
          <td class="d-none d-lg-table-cell text-center" v-if="!ignoreSignature && hasBestBeforeOnAnyLine"><BestBeforeField :stock="stockChange" :ignoreLeadTime="true" /></td>
          <td class="d-none d-lg-table-cell text-center" v-if="!ignoreSignature && hasExpiryOnAnyLine"><ExpirationField :stock="stockChange" :ignoreLeadTime="true" /></td>

          <td class="d-table-cell d-lg-none" v-if="!ignoreSignature">
            <LotField :item="stockChange.item" :lot="stockChange.lot" />
            <span v-if="hasBestBeforeOnAnyLine"><BestBeforeField :stock="stockChange" :ignoreLeadTime="true" /></span>
            <span v-if="hasExpiryOnAnyLine"><ExpirationField :stock="stockChange" :ignoreLeadTime="true" /></span>
          </td>

          <AuditCreatedField :audit="stockChange.audit" class="d-none d-xl-table-cell" />

          <td v-if="userSettingsStore.data.developerMode" class="d-none d-xl-table-cell text-end">
            <button
              class="btn btn-outline-danger btn-sm"
              :title="$t('action.undoStockChange')"
              :disabled="isLoading || undoingId === stockChange.id || ignoreSignature"
              @click="undoStockChange(stockChange)">
              <div v-if="undoingId === stockChange.id" class="spinner-border spinner-border-sm"></div>
              <font-awesome-icon v-else :icon="['fas', 'rotate-left']" />
            </button>
          </td>
        </tr>
        <tr v-if="bottomPadHeight > 0" aria-hidden="true">
          <td :colspan="100" :style="{ padding: 0, border: 0, height: bottomPadHeight + 'px' }"></td>
        </tr>
        <tr v-if="moreItems">
          <td :colspan="100" class="text-center">
            <button class="btn btn-outline-primary" @click="loadNextPage()" :disabled="isLoading">
              {{ $t('action.moreEllipsis') }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
  <div v-else class="loading">
    {{ $t('status.loadingEllipsis') }}
  </div>
</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { storeToRefs } from 'pinia';
import Semaphore from "@/services/semaphore";
import { useSessionStore, useStockChangeStore, useItemsStore, useUserSettingsStore } from '@/stores';
import QuantityChangeCell from "@/components/cells/QuantityChangeCell.vue";
import { AuditCreatedField, BestBeforeField, ExpirationField, LotField, ItemField } from "@/components/fields";
import FrontendStockChange from "@/models/frontend/StockChange";
import TypeaheadComponent from "@/components/TypeaheadComponent.vue";
import Item from "@/models/frontend/Item";
import { router } from "@/services/router";

const { t } = useI18n();
const sessionStore = useSessionStore();
const userSettingsStore = useUserSettingsStore();
const stockChangesStore = useStockChangeStore();
const itemsStore = useItemsStore();

const { selectedSiteId } = storeToRefs(sessionStore);
const { data: stockChanges, error, moreItems } = storeToRefs(stockChangesStore);
const { data: availableItems } = storeToRefs(itemsStore);

const pageSize = 100;
const currentPage = ref(0);
const isLoading = ref(false);
const data = ref<FrontendStockChange[]>([]);
const sort = ref(false);
const baseSortedList = ref<FrontendStockChange[]>([]);
const selectedItem = ref<Item | null>(null);
const semaphore = new Semaphore(1);
const offsetTop = ref(0);
const ignoreSignature = ref(false);
const undoingId = ref<number | null>(null);

const scrollContainerRef = ref<HTMLElement | null>(null);
const rowRefs = ref<HTMLElement[]>([]);
const rowHeight = ref(40);
const scrollTop = ref(0);
const viewportHeight = ref(0);
const overscan = 10;

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const visibleRange = computed(() => {
  const rh = rowHeight.value || 1;
  const total = displayList.value.length;
  const start = Math.max(0, Math.floor(scrollTop.value / rh) - overscan);
  const visibleCount = Math.ceil(viewportHeight.value / rh) + 2 * overscan;
  const end = Math.min(total, start + visibleCount);
  return { start, end };
});

const visibleRows = computed(() => displayList.value.slice(visibleRange.value.start, visibleRange.value.end));
const topPadHeight = computed(() => visibleRange.value.start * rowHeight.value);
const bottomPadHeight = computed(() => Math.max(0, displayList.value.length - visibleRange.value.end) * rowHeight.value);

function syncViewportMetrics() {
  const el = scrollContainerRef.value;
  if (!el) return;
  scrollTop.value = el.scrollTop;
  viewportHeight.value = el.clientHeight;
}

function onScroll() {
  syncViewportMetrics();
  // Auto-load more when scrolled near the bottom of the loaded range
  const rh = rowHeight.value || 1;
  const lastVisibleRow = (scrollTop.value + viewportHeight.value) / rh;
  if (displayList.value.length - lastVisibleRow < overscan && moreItems.value && !isLoading.value) {
    loadNextPage();
  }
}

async function measureRowHeight() {
  await nextTick();
  const sample = rowRefs.value?.[0];
  if (sample) {
    const h = sample.getBoundingClientRect().height;
    if (h > 0 && Math.abs(h - rowHeight.value) > 0.5) {
      rowHeight.value = h;
    }
  }
}

const onWindowResize = () => {
  syncViewportMetrics();
  measureRowHeight();
};

onMounted(async () => {
  currentPage.value = 0;
  await refresh();
  offsetTop.value = scrollContainerRef.value?.getBoundingClientRect().top ?? 0;
  await nextTick();
  syncViewportMetrics();
  await measureRowHeight();
  window.addEventListener('resize', onWindowResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize);
});

const maxQuantityLength = computed(() => {
  return displayList.value.reduce((acc, item) => Math.max(acc, item.quantity.toString().length), 0);
});

async function loadNextPage() {
  isLoading.value = true;
  const currentLength = filteredList.value.length;
  await semaphore.runWithSemaphore(async () => {
    while (moreItems.value && filteredList.value.length <= currentLength) {
      await addPage();
    }
  });
  isLoading.value = false;
}

async function refresh() {
  isLoading.value = true;
  error.value = "";

  try {
    currentPage.value = 0;
    await stockChangesStore.fetchPage(pageSize);
    data.value = stockChanges.value;
  }
  finally {
    isLoading.value = false;
  }
}

async function addPage() {
  isLoading.value = true;
  error.value = "";

  try {
    currentPage.value++;
    if (selectedItem.value) {
      await stockChangesStore.fetchPageAdditiveByItem(selectedItem.value.id, pageSize);
    }
    else {
      await stockChangesStore.fetchPageAdditive(pageSize);
    }
    data.value = stockChanges.value;
  }
  finally {
    isLoading.value = false;
  }
}

const resetFilter = async () => {
  selectedItem.value = null;
  await refresh();
}

async function undoStockChange(stockChange: FrontendStockChange) {
  if (undoingId.value !== null) return;
  if (!window.confirm(t('instruction.undoStockChangeConfirm'))) return;

  undoingId.value = stockChange.id;
  try {
    await stockChangesStore.undoStockChange(stockChange.id);
    await refresh();
  }
  finally {
    undoingId.value = null;
  }
}

// Function to project item display text for typeahead
const itemProjection = (item: Item | null): string => {
  if (!item) return "";
  return `${item.name} (${item.code})`;
}

// Function called when an item is selected from the dropdown
const onItemSelected = async (item: Item | null) => {
  selectedItem.value = item;
  
  if (item) {
    router.push({ name: 'stock-change', params: { itemId: item.id } });
  }
}

const hasBestBeforeOnAnyLine = computed(() => {
  return data.value !== undefined && data.value.findIndex((x) => !!x.bestBefore) !== -1;
});

const hasExpiryOnAnyLine = computed(() => {
  return data.value !== undefined && data.value.findIndex((x) => !!x.expiryAsDate) !== -1;
});

const sortedList = computed(() => {
  if (sort.value) {
    return baseSortedList.value
  }
  else {
    return data.value;
  }
});

const filteredList = computed(() => {
  return sortedList.value.filter((item) => !selectedItem.value || item.item.id === selectedItem.value.id);
});

const displayList = computed<FrontendStockChange[]>(() => {
  if (!ignoreSignature.value) {
    return filteredList.value;
  }

  // Compute item-level totals across all signatures from the loaded data:
  // sum the latest known balance for each unique (item, lot, bestBefore, expiry) signature.
  const sortedAsc = [...filteredList.value].sort((a, b) => a.id - b.id);
  const latestBySignature = new Map<string, FrontendStockChange>();
  for (const change of sortedAsc) {
    const key = `${change.item.id}|${change.lot ?? ''}|${change.bestBefore ?? ''}|${change.expiry ?? ''}`;
    latestBySignature.set(key, change);
  }
  const itemPostTotals = new Map<number, number>();
  for (const change of latestBySignature.values()) {
    itemPostTotals.set(change.item.id, (itemPostTotals.get(change.item.id) ?? 0) + change.quantity);
  }

  // Walk newest-first; the running map currently holds the post-change item total
  // for the most recent row of each item. After emitting a row, subtract its relative
  // change so the map then reflects the post-change total for the next older row.
  return filteredList.value.map((change) => {
    const itemId = change.item.id;
    const postTotal = itemPostTotals.get(itemId) ?? 0;
    itemPostTotals.set(itemId, postTotal - change.realativeQuantity);
    return { ...change, quantity: postTotal } as FrontendStockChange;
  });
});

watch([selectedItem], () => {
  if (scrollContainerRef.value) {
    scrollContainerRef.value.scrollTop = 0;
    scrollTop.value = 0;
  }
  if (moreItems.value) {
    nextTick(async () => {
      loadNextPage();
    });
  }
});

watch(() => filteredList.value.length, async () => {
  await measureRowHeight();
});
</script>
